
import React, { useState } from 'react';

interface OnboardingProps {
  onComplete: () => void;
}

const Onboarding: React.FC<OnboardingProps> = ({ onComplete }) => {
  const [slide, setSlide] = useState(0);
  const [permissions, setPermissions] = useState({ gps: false, mic: false });
  const [isRequesting, setIsRequesting] = useState(false);

  const requestGPS = () => {
    setIsRequesting(true);
    navigator.geolocation.getCurrentPosition(
      () => {
        setPermissions(prev => ({ ...prev, gps: true }));
        setIsRequesting(false);
        setTimeout(() => setSlide(2), 500);
      },
      (err) => {
        console.warn(err);
        setIsRequesting(false);
        // Advance anyway for demo, but mark as failed visually if we wanted
        setSlide(2); 
      }
    );
  };

  const requestMic = () => {
    setIsRequesting(true);
    navigator.mediaDevices.getUserMedia({ audio: true })
      .then(() => {
        setPermissions(prev => ({ ...prev, mic: true }));
        setIsRequesting(false);
        setTimeout(() => setSlide(3), 500);
      })
      .catch((err) => {
        console.warn(err);
        setIsRequesting(false);
        setSlide(3);
      });
  };

  const slides = [
    // SLIDE 0: INTRO
    <div className="flex flex-col items-center text-center space-y-6 animate-fade-in">
      <div className="w-32 h-32 relative">
        <div className="absolute inset-0 border-4 border-primary/30 rounded-full animate-[spin_10s_linear_infinite]"></div>
        <div className="absolute inset-2 border-4 border-primary/60 rounded-full border-t-transparent animate-[spin_3s_linear_infinite]"></div>
        <div className="absolute inset-0 flex items-center justify-center">
            <svg className="w-16 h-16 text-primary drop-shadow-[0_0_15px_rgba(0,240,255,0.8)]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
        </div>
      </div>
      <div>
        <h1 className="text-3xl font-bold tracking-[0.2em] text-white mb-2">Protectly</h1>
        <p className="text-primary font-mono text-xs tracking-widest">DIGITAL GUARDIAN SYSTEM</p>
      </div>
      <p className="max-w-xs text-sm text-gray-400 leading-relaxed">
        Next-generation disaster early warning and tactical family safety network.
        <br/><br/>
        <span className="text-white">Connecting...</span>
      </p>
      <button onClick={() => setSlide(1)} className="w-full max-w-[200px] py-4 bg-primary text-black font-bold tracking-widest text-xs rounded hover:bg-white transition-colors relative overflow-hidden group">
          <span className="relative z-10">INITIALIZE</span>
          <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform"></div>
      </button>
    </div>,

    // SLIDE 1: GPS
    <div className="flex flex-col items-center text-center space-y-6 animate-fade-in">
       <div className="w-24 h-24 rounded-full bg-black/50 border border-white/10 flex items-center justify-center mb-4 relative overflow-hidden">
           <div className="absolute inset-0 bg-primary/10 animate-pulse"></div>
           <svg className="w-10 h-10 text-primary relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
       </div>
       <h2 className="text-xl font-bold text-white tracking-widest uppercase">Orbital Uplink</h2>
       <p className="max-w-xs text-xs text-gray-400 font-mono">
           Grant GPS access to enable real-time hazard triangulation and Squad tracking.
       </p>
       <button 
        onClick={requestGPS}
        disabled={isRequesting}
        className="w-full max-w-[240px] py-4 border border-primary text-primary font-bold tracking-widest text-xs rounded hover:bg-primary hover:text-black transition-colors"
       >
          {isRequesting ? 'ACQUIRING SIGNAL...' : 'ENABLE GEOLOCATION'}
       </button>
       <button onClick={() => setSlide(2)} className="text-[10px] text-gray-600 underline">SKIP UPLINK (SIMULATION MODE)</button>
    </div>,

    // SLIDE 2: MIC
    <div className="flex flex-col items-center text-center space-y-6 animate-fade-in">
       <div className="w-24 h-24 rounded-full bg-black/50 border border-white/10 flex items-center justify-center mb-4 relative">
           <div className="absolute inset-0 border border-white/5 rounded-full animate-ping"></div>
           <svg className="w-10 h-10 text-secondary relative z-10 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" /></svg>
       </div>
       <h2 className="text-xl font-bold text-white tracking-widest uppercase">Audio Sensors</h2>
       <p className="max-w-xs text-xs text-gray-400 font-mono">
           Enable microphone access for SOS Beacon broadcasting and AI voice commands.
       </p>
       <button 
        onClick={requestMic}
        disabled={isRequesting}
        className="w-full max-w-[240px] py-4 border border-purple-500 text-purple-500 font-bold tracking-widest text-xs rounded hover:bg-purple-500 hover:text-white transition-colors"
       >
          {isRequesting ? 'CALIBRATING...' : 'ENABLE SENSORS'}
       </button>
       <button onClick={() => setSlide(3)} className="text-[10px] text-gray-600 underline">SKIP SENSORS</button>
    </div>,

    // SLIDE 3: READY
    <div className="flex flex-col items-center text-center space-y-8 animate-fade-in">
        <div className="space-y-2">
            <div className="flex items-center gap-3 text-xs font-mono">
                <span className={`w-2 h-2 rounded-full ${permissions.gps ? 'bg-success' : 'bg-red-500'}`}></span>
                <span className="text-gray-400">GPS UPLINK:</span>
                <span className={permissions.gps ? 'text-success' : 'text-red-500'}>{permissions.gps ? 'CONNECTED' : 'OFFLINE'}</span>
            </div>
            <div className="flex items-center gap-3 text-xs font-mono">
                <span className={`w-2 h-2 rounded-full ${permissions.mic ? 'bg-success' : 'bg-red-500'}`}></span>
                <span className="text-gray-400">AUDIO SENSORS:</span>
                <span className={permissions.mic ? 'text-success' : 'text-red-500'}>{permissions.mic ? 'ACTIVE' : 'DISABLED'}</span>
            </div>
            <div className="flex items-center gap-3 text-xs font-mono">
                <span className="w-2 h-2 rounded-full bg-success"></span>
                <span className="text-gray-400">NEURAL CORE:</span>
                <span className="text-success">ONLINE</span>
            </div>
        </div>

        <h1 className="text-4xl font-bold text-white tracking-tighter">SYSTEM READY</h1>

        <button 
            onClick={onComplete}
            className="w-full max-w-[240px] py-5 bg-white text-black font-bold tracking-[0.3em] text-sm rounded shadow-[0_0_30px_rgba(255,255,255,0.4)] hover:scale-105 transition-transform"
        >
            ENTER SYSTEM
        </button>
    </div>
  ];

  return (
    <div className="fixed inset-0 z-[200] bg-black text-white flex flex-col">
        {/* Background Grid */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(0,240,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(0,240,255,0.03)_1px,transparent_1px)] bg-[size:50px_50px]"></div>
        
        {/* Progress Bar */}
        <div className="absolute top-0 left-0 w-full h-1 bg-gray-900">
            <div className="h-full bg-primary transition-all duration-500" style={{ width: `${((slide + 1) / 4) * 100}%` }}></div>
        </div>

        <div className="flex-1 flex items-center justify-center p-6 relative z-10">
            {slides[slide]}
        </div>

        {/* Footer */}
        <div className="p-6 text-center text-[9px] font-mono text-gray-600 tracking-widest">
            SECURE CONNECTION v3.1.4 // Protectly CORP
        </div>
    </div>
  );
};

export default Onboarding;
